import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useSelector } from 'react-redux';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import EmptyContent from '../../../components/empty-content';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import { getUserRoles } from '../../../services/employeeService';
import { getAllCompanies } from '../../../services/leaveService';
import { getSurveyResponses } from '../../../services/surveyService';
import { isSuperAdmin } from '../../../utils/share';

const DEFAULT_QUERY = {
  pageNumber: 1,
  pageSize: 10,
  search: '',
  periodKey: '',
  userRoleId: '',
  companyId: '',
  surveyDateFrom: '',
  surveyDateTo: '',
  orderBy: 'submittedAt',
  order: 'DESC',
};

const getDefaultQuery = (enforcedCompanyId = '') => ({
  ...DEFAULT_QUERY,
  companyId: enforcedCompanyId,
});

const SORT_OPTIONS = [
  { value: 'submittedAt', label: 'Submitted at' },
  { value: 'surveyDate', label: 'Survey date' },
  { value: 'periodKey', label: 'Period key' },
];

const ORDER_OPTIONS = [
  { value: 'DESC', label: 'Newest first' },
  { value: 'ASC', label: 'Oldest first' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const parsePositiveNumber = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeAnswerValue = (answer) => {
  if (answer?.answerText?.trim()) {
    return {
      type: 'text',
      value: answer.answerText.trim(),
    };
  }

  if (answer?.answerJson) {
    return {
      type: 'json',
      value: typeof answer.answerJson === 'string' ? answer.answerJson : JSON.stringify(answer.answerJson, null, 2),
    };
  }

  return {
    type: 'empty',
    value: 'No response',
  };
};

const sortAnswers = (answers = []) =>
  [...answers].sort((firstAnswer, secondAnswer) => {
    const firstOrder = firstAnswer?.question?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const secondOrder = secondAnswer?.question?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return firstOrder - secondOrder;
  });

const formatDateLabel = (value) => (value ? format(new Date(value), 'dd MMM yyyy') : 'N/A');

const formatDateTimeLabel = (value) => (value ? format(new Date(value), 'dd MMM yyyy, hh:mm a') : 'N/A');

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (text, query) => {
  const content = text ?? '';

  if (!content || !query?.trim()) {
    return content || 'N/A';
  }

  const matcher = new RegExp(`(${escapeRegExp(query.trim())})`, 'ig');
  const parts = String(content).split(matcher);
  const normalizedQuery = query.trim().toLowerCase();

  return parts.map((part, index) =>
    part.toLowerCase() === normalizedQuery ? (
      <Box
        key={`${part}-${index}`}
        component="mark"
        sx={{
          px: 0.25,
          borderRadius: 0.5,
          backgroundColor: '#FFF3BF',
          color: 'inherit',
        }}
      >
        {part}
      </Box>
    ) : (
      <Box key={`${part}-${index}`} component="span">
        {part}
      </Box>
    )
  );
};

const buildInitialQuery = (searchParams, canFilterCompany, enforcedCompanyId = '') => {
  const defaultQuery = getDefaultQuery(enforcedCompanyId);
  const orderBy = SORT_OPTIONS.find((option) => option.value === searchParams.get('orderBy'))?.value ?? DEFAULT_QUERY.orderBy;
  const order = ORDER_OPTIONS.find((option) => option.value === searchParams.get('order'))?.value ?? DEFAULT_QUERY.order;
  const pageSize = PAGE_SIZE_OPTIONS.includes(parsePositiveNumber(searchParams.get('pageSize'), DEFAULT_QUERY.pageSize))
    ? parsePositiveNumber(searchParams.get('pageSize'), DEFAULT_QUERY.pageSize)
    : DEFAULT_QUERY.pageSize;

  return {
    pageNumber: parsePositiveNumber(searchParams.get('pageNumber'), defaultQuery.pageNumber),
    pageSize,
    search: searchParams.get('search') ?? defaultQuery.search,
    periodKey: searchParams.get('periodKey') ?? defaultQuery.periodKey,
    userRoleId: searchParams.get('userRoleId') ?? defaultQuery.userRoleId,
    companyId: canFilterCompany ? searchParams.get('companyId') ?? defaultQuery.companyId : enforcedCompanyId,
    surveyDateFrom: searchParams.get('surveyDateFrom') ?? defaultQuery.surveyDateFrom,
    surveyDateTo: searchParams.get('surveyDateTo') ?? defaultQuery.surveyDateTo,
    orderBy,
    order,
  };
};

function SurveyFilters({
  searchInput,
  query,
  onSearchChange,
  onFilterChange,
  onResetFilters,
  roleOptions,
  companyOptions,
  canFilterCompany,
  activeFilterCount,
}) {
  const hasActiveFilters = activeFilterCount > 0;

  return (
    <Card
      sx={{
        mb: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        background: 'linear-gradient(135deg, rgba(255,249,235,0.95) 0%, rgba(242,248,255,0.95) 100%)',
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Survey responses
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search submissions, narrow them down quickly, and open a focused answer view when you need the full context.
            </Typography>
          </Box>

          <Chip label={`${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'}`} color="default" />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search submissions"
              placeholder="Name, email, answers, questions..."
              value={searchInput}
              onChange={(event) => onSearchChange(event.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon
                    sx={{
                      mr: 1,
                      color: 'text.secondary',
                    }}
                  />
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Period key"
              placeholder="2026-14"
              value={query.periodKey}
              onChange={(event) => onFilterChange('periodKey', event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Role"
              value={query.userRoleId}
              onChange={(event) => onFilterChange('userRoleId', event.target.value)}
            >
              <MenuItem value="">All roles</MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.roleName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {canFilterCompany && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Company"
                value={query.companyId}
                onChange={(event) => onFilterChange('companyId', event.target.value)}
              >
                <MenuItem value="">All companies</MenuItem>
                {companyOptions.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={canFilterCompany ? 2 : 3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Survey date from"
                inputFormat="DD MMM YYYY"
                value={query.surveyDateFrom ? dayjs(query.surveyDateFrom) : null}
                onChange={(date) => {
                  const formattedDate = date ? date.format('YYYY-MM-DD') : '';
                  onFilterChange('surveyDateFrom', formattedDate);

                  if (query.surveyDateTo && formattedDate && dayjs(query.surveyDateTo).isBefore(date, 'day')) {
                    onFilterChange('surveyDateTo', '');
                  }
                }}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={canFilterCompany ? 2 : 3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Survey date to"
                inputFormat="DD MMM YYYY"
                value={query.surveyDateTo ? dayjs(query.surveyDateTo) : null}
                minDate={query.surveyDateFrom ? dayjs(query.surveyDateFrom) : undefined}
                onChange={(date) => onFilterChange('surveyDateTo', date ? date.format('YYYY-MM-DD') : '')}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Sort by"
              value={query.orderBy}
              onChange={(event) => onFilterChange('orderBy', event.target.value)}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Order"
              value={query.order}
              onChange={(event) => onFilterChange('order', event.target.value)}
            >
              {ORDER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              color="inherit"
              variant={hasActiveFilters ? 'outlined' : 'text'}
              onClick={onResetFilters}
              disabled={!hasActiveFilters}
            >
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

SurveyFilters.propTypes = {
  activeFilterCount: PropTypes.number.isRequired,
  canFilterCompany: PropTypes.bool.isRequired,
  companyOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchInput: PropTypes.string.isRequired,
};

function SurveyResponseCard({ response, searchTerm, onOpenDetails, onCopyEmail, isSelected }) {
  const sortedAnswers = sortAnswers(response.answers);
  const firstAnswer = sortedAnswers[0];
  const firstAnswerValue = firstAnswer ? normalizeAnswerValue(firstAnswer).value : 'No answers submitted';
  const userName = response?.user?.userName || 'Unknown employee';
  const email = response?.user?.email || 'No email';
  const companyName = response?.user?.company?.name || 'No company';
  const roleName = response?.user?.userRoles?.roleName || 'No role';

  return (
    <Card
      sx={{
        border: (theme) => `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        boxShadow: isSelected ? (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}` : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `0 10px 24px ${alpha(theme.palette.common.black, 0.08)}`,
        },
      }}
    >
      <CardActionArea onClick={() => onOpenDetails(response)} sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2.5}>
          <Stack spacing={2} flex={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ bgcolor: '#1F6FEB', color: 'common.white' }}>
                  {userName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {highlightText(userName, searchTerm)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <MailOutlineRoundedIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{highlightText(email, searchTerm)}</Typography>
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={companyName} variant="outlined" size="small" />
                <Chip label={roleName} variant="outlined" size="small" />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Period ${response.periodKey || 'N/A'}`}
                sx={{ bgcolor: '#FFF4D6', color: '#8A5A00', fontWeight: 600 }}
              />
              <Chip label={`Survey ${formatDateLabel(response.surveyDate)}`} variant="outlined" />
              <Chip label={`Submitted ${formatDateTimeLabel(response.submittedAt)}`} variant="outlined" />
              <Chip label={`${sortedAnswers.length} answer${sortedAnswers.length === 1 ? '' : 's'}`} variant="outlined" />
            </Stack>

            <Box
              sx={{
                p: 1.75,
                borderRadius: 2,
                backgroundColor: '#FCF8EF',
                border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                {firstAnswer?.question?.questionText || 'Answer preview'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  display: '-webkit-box',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
            
                }}
              >
                {highlightText(firstAnswerValue, searchTerm)}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'row', lg: 'column' }} justifyContent="space-between" alignItems={{ xs: 'center', lg: 'flex-end' }}>
            <Tooltip title="Copy email">
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onCopyEmail(email);
                }}
              >
                <ContentCopyOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button endIcon={<KeyboardArrowRightRoundedIcon />} sx={{ whiteSpace: 'nowrap' }}>
              View answers
            </Button>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

SurveyResponseCard.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  onCopyEmail: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func.isRequired,
  response: PropTypes.object.isRequired,
  searchTerm: PropTypes.string.isRequired,
};

function SurveyResponseDetailsDrawer({ open, response, searchTerm, onClose, onCopyEmail }) {
  const sortedAnswers = useMemo(() => sortAnswers(response?.answers), [response?.answers]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, lg: 680 },
        },
      }}
    >
      {response ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              background: 'linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,1) 100%)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ mb: 0.75 }}>
                  {response?.user?.userName || 'Unknown employee'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {response?.user?.email || 'No email'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={response?.user?.company?.name || 'No company'} variant="outlined" size="small" />
                  <Chip label={response?.user?.userRoles?.roleName || 'No role'} variant="outlined" size="small" />
                  <Chip label={`Period ${response?.periodKey || 'N/A'}`} size="small" sx={{ bgcolor: '#FFF4D6', color: '#8A5A00' }} />
                  <Chip label={`Survey ${formatDateLabel(response?.surveyDate)}`} variant="outlined" size="small" />
                  <Chip label={`Submitted ${formatDateTimeLabel(response?.submittedAt)}`} variant="outlined" size="small" />
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Tooltip title="Copy email">
                  <IconButton onClick={() => onCopyEmail(response?.user?.email || '')}>
                    <ContentCopyOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Submitted answers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sortedAnswers.length} answer{sortedAnswers.length === 1 ? '' : 's'} in this submission
                </Typography>
              </Box>

              {sortedAnswers.length ? (
                sortedAnswers.map((answer, index) => {
                  const answerValue = normalizeAnswerValue(answer);

                  return (
                    <Card
                      key={answer.id || answer?.question?.id || `${answer.questionId}-${index}`}
                      sx={{
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                        boxShadow: 'none',
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {highlightText(answer?.question?.questionText || 'Untitled question', searchTerm)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {`Question ID ${answer?.question?.id ?? answer?.questionId ?? 'N/A'}`} |{' '}
                                {answer?.question?.type || 'text'}
                              </Typography>
                            </Box>
                            <Chip label={`Question ${index + 1}`} size="small" variant="outlined" />
                          </Stack>

                          <Divider />

                          {answerValue.type === 'json' ? (
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 2,
                                overflowX: 'auto',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                borderRadius: 1.5,
                                backgroundColor: '#0F172A',
                                color: '#E2E8F0',
                                fontSize: 13,
                                lineHeight: 1.7,
                              }}
                            >
                              {answerValue.value}
                            </Box>
                          ) : (
                            <Typography
                              variant="body1"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                color: answerValue.type === 'empty' ? 'text.secondary' : 'text.primary',
                                lineHeight: 1.8,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                              }}
                            >
                              {highlightText(answerValue.value, searchTerm)}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <EmptyContent
                  filled
                  title="No answers on this submission"
                  description="This survey submission did not include any answer rows."
                  sx={{ py: 8 }}
                />
              )}
            </Stack>
          </Box>
        </Box>
      ) : null}
    </Drawer>
  );
}

SurveyResponseDetailsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCopyEmail: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  response: PropTypes.object,
  searchTerm: PropTypes.string.isRequired,
};

SurveyResponseDetailsDrawer.defaultProps = {
  response: null,
};

function SurveyResponses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state?.login?.user || null);
  const isSuperAdminUser = isSuperAdmin();
  const enforcedCompanyId = !isSuperAdminUser && currentUser?.companyId ? String(currentUser.companyId) : '';
  const canFilterCompany = isSuperAdminUser;
  const [query, setQuery] = useState(() => buildInitialQuery(searchParams, canFilterCompany, enforcedCompanyId));
  const [searchInput, setSearchInput] = useState(
    () => buildInitialQuery(searchParams, canFilterCompany, enforcedCompanyId).search
  );
  const [responses, setResponses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [roleOptions, setRoleOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isMetaLoading, setIsMetaLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Admin', path: '/admin/employees-timeline' },
    { label: 'Survey Responses', path: '/admin/survey-responses' },
  ];

  const activeFilterCount = useMemo(
    () =>
      [
        query.search,
        query.periodKey,
        query.userRoleId,
        canFilterCompany ? query.companyId : '',
        query.surveyDateFrom,
        query.surveyDateTo,
      ].filter(Boolean).length,
    [canFilterCompany, query.companyId, query.periodKey, query.search, query.surveyDateFrom, query.surveyDateTo, query.userRoleId]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / query.pageSize));
  const pageStart = totalCount ? (query.pageNumber - 1) * query.pageSize + 1 : 0;
  const pageEnd = totalCount ? Math.min(query.pageNumber * query.pageSize, totalCount) : 0;

  const updateQuery = useCallback((updater) => {
    setQuery((previousQuery) => {
      const nextQuery = typeof updater === 'function' ? updater(previousQuery) : updater;
      return nextQuery;
    });
  }, []);

  const fetchMeta = useCallback(async () => {
    setIsMetaLoading(true);

    try {
      const requests = [getUserRoles()];

      if (canFilterCompany) {
        requests.push(getAllCompanies());
      }

      const [rolesResponse, companiesResponse] = await Promise.all(requests);
      setRoleOptions(rolesResponse?.data?.data || []);
      setCompanyOptions(companiesResponse?.data?.data || []);
    } catch (error) {
      toast.error('Unable to load some filter options right now.');
    } finally {
      setIsMetaLoading(false);
    }
  }, [canFilterCompany]);

  const fetchResponseList = useCallback(
    async (currentQuery) => {
      const shouldShowInitialLoader = !responses.length && currentQuery.pageNumber === 1;

      setErrorMessage('');
      if (shouldShowInitialLoader) {
        setIsInitialLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const response = await getSurveyResponses(currentQuery);
        const payload = response?.data?.data || {};
        const nextRows = (payload.rows || []).map((item) => ({
          ...item,
          answers: sortAnswers(item.answers),
        }));

        setResponses(nextRows);
        setTotalCount(payload.count || 0);

        if (currentQuery.pageNumber > 1 && nextRows.length === 0 && payload.count > 0) {
          updateQuery((previousQuery) => ({
            ...previousQuery,
            pageNumber: Math.max(1, Math.ceil(payload.count / previousQuery.pageSize)),
          }));
        }
      } catch (error) {
        setErrorMessage(error?.response?.data?.message || 'Unable to fetch survey responses right now.');
        setResponses([]);
        setTotalCount(0);
      } finally {
        setIsInitialLoading(false);
        setIsRefetching(false);
      }
    },
    [responses.length, updateQuery]
  );

  const syncQueryParams = useCallback(
    (currentQuery) => {
      const nextParams = new URLSearchParams();

      Object.entries(currentQuery).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (
            (key === 'pageNumber' && value === DEFAULT_QUERY.pageNumber) ||
            (key === 'pageSize' && value === DEFAULT_QUERY.pageSize) ||
            (key === 'orderBy' && value === DEFAULT_QUERY.orderBy) ||
            (key === 'order' && value === DEFAULT_QUERY.order) ||
            (key === 'companyId' && !canFilterCompany)
          ) {
            return;
          }

          nextParams.set(key, String(value));
        }
      });

      setSearchParams(nextParams, { replace: true });
    },
    [canFilterCompany, setSearchParams]
  );

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    if (!canFilterCompany && enforcedCompanyId && query.companyId !== enforcedCompanyId) {
      setQuery((previousQuery) => ({
        ...previousQuery,
        companyId: enforcedCompanyId,
        pageNumber: 1,
      }));
    }
  }, [canFilterCompany, enforcedCompanyId, query.companyId]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      updateQuery((previousQuery) => {
        if (previousQuery.search === searchInput.trim()) {
          return previousQuery;
        }

        return {
          ...previousQuery,
          search: searchInput.trim(),
          pageNumber: 1,
        };
      });
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [searchInput, updateQuery]);

  useEffect(() => {
    syncQueryParams(query);
    fetchResponseList(query);
  }, [fetchResponseList, query, syncQueryParams]);

  useEffect(() => {
    if (!selectedResponse) {
      return;
    }

    const refreshedSelection = responses.find((item) => item.id === selectedResponse.id);
    if (refreshedSelection) {
      setSelectedResponse(refreshedSelection);
    }
  }, [responses, selectedResponse]);

  const handleFilterChange = (field, value) => {
    updateQuery((previousQuery) => ({
      ...previousQuery,
      [field]: value,
      pageNumber: field === 'pageNumber' ? value : 1,
    }));
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setQuery(getDefaultQuery(enforcedCompanyId));
  };

  const handleOpenDetails = (response) => {
    setSelectedResponse(response);
    setIsDrawerOpen(true);
  };

  const handleCopyEmail = async (email) => {
    if (!email) {
      toast.info('No email available for this submission.');
      return;
    }

    try {
      await navigator.clipboard.writeText(email);
      toast.success('Email copied to clipboard.');
    } catch (error) {
      toast.error('Unable to copy the email right now.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Survey Responses</title>
      </Helmet>

      <BreadCrumb breadcrumbs={breadcrumbs} />

      <Container maxWidth="xl" sx={{ pb: 4,pt: 2 }}>
        <SurveyFilters
          searchInput={searchInput}
          query={query}
          onSearchChange={setSearchInput}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          roleOptions={roleOptions}
          companyOptions={companyOptions}
          canFilterCompany={canFilterCompany}
          activeFilterCount={activeFilterCount}
        />

        <Card
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          {(isRefetching || isMetaLoading) && <LinearProgress />}

          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.25,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: '#FCFCFD',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography variant="h6">Submission list</Typography>
                <Typography variant="body2" color="text.secondary">
                  {totalCount
                    ? `Showing ${pageStart}-${pageEnd} of ${totalCount} submissions`
                    : 'No submissions matched the current filters'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Page ${query.pageNumber} of ${totalPages}`} variant="outlined" size="small" />
                <Chip label={`Sorted by ${SORT_OPTIONS.find((option) => option.value === query.orderBy)?.label}`} size="small" />
                {currentUser?.companyId && !query.companyId && !canFilterCompany && (
                  <Chip label="Company scoped" variant="outlined" size="small" />
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {errorMessage ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => fetchResponseList(query)}>
                    Retry
                  </Button>
                }
              >
                {errorMessage}
              </Alert>
            ) : null}

            {isInitialLoading ? (
              <Stack spacing={2}>
                {[1, 2, 3].map((item) => (
                  <Card key={item} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Skeleton variant="circular" width={42} height={42} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="35%" height={24} />
                            <Skeleton width="28%" height={20} />
                          </Box>
                        </Stack>
                        <Skeleton width="65%" height={22} />
                        <Skeleton width="100%" height={68} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : responses.length ? (
              <Stack spacing={2}>
                {responses.map((response) => (
                  <SurveyResponseCard
                    key={response.id}
                    response={response}
                    searchTerm={query.search}
                    onOpenDetails={handleOpenDetails}
                    onCopyEmail={handleCopyEmail}
                    isSelected={selectedResponse?.id === response.id && isDrawerOpen}
                  />
                ))}
              </Stack>
            ) : (
              <EmptyContent
                filled
                title="No survey submissions found"
                description="Try clearing a few filters or changing the search term to widen the results."
                sx={{ py: 10 }}
              />
            )}
          </Box>

          <Divider />

          <TablePaginationCustom
            count={totalCount}
            page={Math.max(query.pageNumber - 1, 0)}
            rowsPerPage={query.pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(event, newPage) => handleFilterChange('pageNumber', newPage + 1)}
            onRowsPerPageChange={(event) =>
              updateQuery((previousQuery) => ({
                ...previousQuery,
                pageSize: Number(event.target.value),
                pageNumber: 1,
              }))
            }
          />
        </Card>
      </Container>

      <SurveyResponseDetailsDrawer
        open={isDrawerOpen}
        response={selectedResponse}
        searchTerm={query.search}
        onClose={() => setIsDrawerOpen(false)}
        onCopyEmail={handleCopyEmail}
      />
    </>
  );
}

export default SurveyResponses;
